/**
 * Typed client for the Quotes/Proposals subsystem (epic 131.45). Mirrors
 * internal/quotes, reconciled in 131.45.7 to a legacy-faithful superset:
 * header CRUD with per-(tenant,year) sequential numbering, line items with an
 * MRR-aware money split, a tenant-wide clause/template catalog composed onto
 * each quote's terms snapshot, a guarded status lifecycle (now including
 * VIEWED/REJECTED/SUPERSEDED/DELETED), a version/supersession chain, and an
 * activity log.
 */
import { apiClient } from '@/lib/apiClient';

export type QuoteStatus =
  | 'DRAFT'
  | 'SENT'
  | 'VIEWED'
  | 'ACCEPTED'
  | 'DECLINED'
  | 'REJECTED'
  | 'EXPIRED'
  | 'SUPERSEDED'
  | 'DELETED';

export type LineType = 'PRODUCT' | 'SERVICE' | 'AI_AAS' | 'CUSTOM';

export interface QuoteSummary {
  id: string;
  quoteNumber: string;
  version: number;
  accountId: string;
  opportunityId: string | null;
  title: string;
  status: QuoteStatus;
  currency: string;
  totalOneTime: number;
  totalMrr: number;
  total: number;
  validUntil: string | null;
  createdAt: string;
}

export interface QuoteLineItem {
  id: string;
  productId: string | null;
  lineOrder: number;
  lineType: LineType;
  catalogNaturalId: string | null;
  description: string;
  descriptionMd: string | null;
  unitLabel: string | null;
  quantity: number;
  unitPrice: number;
  discountPercent: number;
  oneTimeTotal: number;
  mrrTotal: number;
  total: number;
}

export interface QuoteActivity {
  eventType: string;
  actorUserId: string | null;
  actorEmail: string | null;
  actorIp: string | null;
  occurredAt: string;
}

export interface QuoteDetail {
  id: string;
  quoteNumber: string;
  version: number;
  parentQuoteId: string | null;
  supersededById: string | null;
  accountId: string;
  opportunityId: string | null;
  title: string;
  status: QuoteStatus;
  currency: string;
  summaryMd: string | null;
  notesInternal: string | null;
  primaryContactId: string | null;
  billingContactId: string | null;
  technicalContactId: string | null;
  ccEmails: string[];
  paymentTerms: string | null;
  billingCadence: string | null;
  validUntil: string | null;
  termsAndConditionsMd: string | null;
  subtotalOneTime: number;
  subtotalMrr: number;
  discountPct: number;
  discountAmount: number;
  taxPct: number;
  taxAmount: number;
  totalOneTime: number;
  totalMrr: number;
  total: number;
  publicToken: string | null;
  createdByUserId: string | null;
  sentAt: string | null;
  firstViewedAt: string | null;
  lastViewedAt: string | null;
  viewCount: number;
  acceptedAt: string | null;
  acceptedByName: string | null;
  acceptedByEmail: string | null;
  declinedAt: string | null;
  rejectedAt: string | null;
  rejectedReason: string | null;
  orderId: string | null;
  createdAt: string;
  updatedAt: string;
  lineItems: QuoteLineItem[];
  activity: QuoteActivity[];
}

export interface Clause {
  id: string;
  clauseKey: string;
  name: string;
  sectionTitle: string;
  appliesTo: string[];
  bodyMd: string;
  sortOrder: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TermTemplate {
  id: string;
  templateKey: string;
  name: string;
  kind: 'PAYMENT_TERMS' | 'BILLING_CADENCE' | 'TERMS_AND_CONDITIONS';
  bodyMd: string | null;
  sortOrder: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateQuoteRequest {
  title: string;
  accountId: string;
  opportunityId?: string | null;
  currency?: string;
  validUntil?: string | null;
  summaryMd?: string | null;
  notesInternal?: string | null;
  paymentTerms?: string | null;
  billingCadence?: string | null;
  discountPct?: number;
  taxPct?: number;
}

export interface UpdateQuoteRequest {
  title: string;
  accountId: string;
  opportunityId?: string | null;
  validUntil?: string | null;
  summaryMd?: string | null;
  notesInternal?: string | null;
  paymentTerms?: string | null;
  billingCadence?: string | null;
  discountPct?: number;
  taxPct?: number;
}

export interface LineItemRequest {
  productId?: string | null;
  lineType?: LineType;
  catalogNaturalId?: string | null;
  description: string;
  descriptionMd?: string | null;
  unitLabel?: string | null;
  quantity: number;
  unitPrice: number;
  discountPercent?: number;
  recurring?: boolean;
}

export async function listQuotes(): Promise<QuoteSummary[]> {
  const { data } = await apiClient.get<QuoteSummary[]>('/quotes');
  return data ?? [];
}

export async function createQuote(req: CreateQuoteRequest): Promise<{ id: string; quoteNumber: string }> {
  const { data } = await apiClient.post<{ id: string; quoteNumber: string }>('/quotes', req);
  return data;
}

export async function getQuote(id: string): Promise<QuoteDetail> {
  const { data } = await apiClient.get<QuoteDetail>(`/quotes/${id}`);
  return data;
}

export async function updateQuote(id: string, req: UpdateQuoteRequest): Promise<void> {
  await apiClient.patch(`/quotes/${id}`, req);
}

export async function deleteQuote(id: string): Promise<void> {
  await apiClient.delete(`/quotes/${id}`);
}

export async function transitionQuote(id: string, status: QuoteStatus, reason?: string): Promise<void> {
  await apiClient.post(`/quotes/${id}/status`, { status, reason: reason ?? null });
}

export async function newQuoteVersion(id: string): Promise<{ id: string; quoteNumber: string }> {
  const { data } = await apiClient.post<{ id: string; quoteNumber: string }>(`/quotes/${id}/new-version`);
  return data;
}

export async function addLineItem(quoteId: string, req: LineItemRequest): Promise<{ id: string }> {
  const { data } = await apiClient.post<{ id: string }>(`/quotes/${quoteId}/line-items`, req);
  return data;
}

export async function deleteLineItem(quoteId: string, lineId: string): Promise<void> {
  await apiClient.delete(`/quotes/${quoteId}/line-items/${lineId}`);
}

export async function composeTerms(quoteId: string, clauseIds: string[], templateId?: string | null): Promise<string> {
  const { data } = await apiClient.post<{ termsAndConditionsMd: string }>(`/quotes/${quoteId}/terms/compose`, {
    clauseIds,
    templateId: templateId ?? null,
  });
  return data.termsAndConditionsMd;
}

export async function listClauses(): Promise<Clause[]> {
  const { data } = await apiClient.get<Clause[]>('/quote-clauses');
  return data ?? [];
}

export async function listTermTemplates(): Promise<TermTemplate[]> {
  const { data } = await apiClient.get<TermTemplate[]>('/quote-term-templates');
  return data ?? [];
}
